import React, { createContext, useContext, useState, useEffect } from 'react';

const CLOUD_TOKEN_KEY = 'ruonn_cloud_token';

// API Gateway base URL - in production this would be api.app.ruonn.com
// In development/preview, it's the same origin with /api prefix
const API_GATEWAY = process.env.REACT_APP_API_GATEWAY_URL || process.env.REACT_APP_BACKEND_URL || '';

const CloudAuthContext = createContext(null);

export function CloudAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // CloudAuthCallback will handle auth_token (direct Google OAuth) or session_id first.
    if (window.location.hash?.includes('auth_token=') || window.location.hash?.includes('session_id=')) {
      setIsLoading(false);
      return;
    }

    const storedToken = localStorage.getItem(CLOUD_TOKEN_KEY);
    if (storedToken) {
      validateToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const validateToken = async (t) => {
    try {
      // Use gateway route: /api/auth/me
      const res = await fetch(`${API_GATEWAY}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${t}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.data);
        setToken(t);
      } else {
        localStorage.removeItem(CLOUD_TOKEN_KEY);
      }
    } catch (error) {
      localStorage.removeItem(CLOUD_TOKEN_KEY);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    // Use gateway route: /api/auth/login
    const res = await fetch(`${API_GATEWAY}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    const newToken = data.data.token.token;
    localStorage.setItem(CLOUD_TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(data.data.user);
    
    return data;
  };

  const logout = () => {
    localStorage.removeItem(CLOUD_TOKEN_KEY);
    setUser(null);
    setToken(null);
  };

  // Set auth data directly (used after registration)
  const setAuthData = (newToken, userData) => {
    localStorage.setItem(CLOUD_TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(userData);
  };

  // API helper for making authenticated requests
  const fetchApi = async (path, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const res = await fetch(`${API_GATEWAY}${path}`, {
      ...options,
      headers
    });

    // Read as text first (more resilient than res.json() with proxies/extensions)
    let text = '';
    let data = {};
    try {
      text = await res.text();
    } catch {
      // Body already consumed by proxy/extension — use status only
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      return data;
    }

    // Parse text as JSON if non-empty
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        // Non-JSON body (e.g. HTML error page from proxy)
        if (!res.ok) throw new Error(text.slice(0, 120) || `Request failed (${res.status})`);
        return data;
      }
    }

    if (!res.ok) {
      throw new Error(data?.detail || data?.error || `Request failed (${res.status})`);
    }
    return data;
  };

  return (
    <CloudAuthContext.Provider value={{ user, token, isLoading, login, logout, setAuthData, fetchApi, apiGateway: API_GATEWAY }}>
      {children}
    </CloudAuthContext.Provider>
  );
}

export function useCloudAuth() {
  const ctx = useContext(CloudAuthContext);
  if (!ctx) throw new Error('useCloudAuth must be used within CloudAuthProvider');
  return ctx;
}
